import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { BackButton, Badge, Button, Card, CardHeader } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import {
  kycReviewService,
  type KycBankAccount,
  type KycDocument,
  type KycDocumentDecision,
  type KycFinancial,
  type KycIndemnity,
  type KycKeyPerson,
} from '@/services/brokerKyc';
import { kycStatusLabel } from '@/pages/broker/brokerKycForm';
import {
  DECISION_LABELS,
  canVerifyDossier,
  decisionNeedsReason,
  documentStateLabel,
  documentStatusTone,
  dossierStatusTone,
  verifyBlockedReason,
  licenceRangeLabel,
} from './kycReview';
import { formatDate } from '@/utils';

/**
 * #389: one broker's compliance / KYC dossier, for the country admin. Each
 * document is approved, rejected or sent back for changes with a reason; the
 * dossier status follows from the documents on the server, and the broker is
 * told about every decision by in-app notification and email.
 */

const inputClass =
  'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white';

function ReadOnlyList<T extends { id: string }>({
  title,
  rows,
  isError,
  render,
}: {
  title: string;
  rows: T[] | undefined;
  isError: boolean;
  render: (row: T) => string;
}) {
  return (
    <Card className="p-6">
      <CardHeader title={title} />
      {isError ? (
        <p role="alert" className="text-sm text-red-600">
          Could not load this list.
        </p>
      ) : !rows?.length ? (
        <p className="text-sm text-gray-500">None on file.</p>
      ) : (
        <ul className="divide-y divide-gray-200 text-sm dark:divide-gray-700">
          {rows.map((row) => (
            <li key={row.id} className="py-2 text-gray-800 dark:text-gray-200">
              {render(row)}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function DocumentRow({ document, onChanged }: { document: KycDocument; onChanged: () => void }) {
  const [decision, setDecision] = useState<KycDocumentDecision | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const review = useMutation({
    mutationFn: () => kycReviewService.reviewDocument(document.id, decision!, reason.trim()),
    onSuccess: () => {
      toast.success(
        `${document.doc_type_display}: ${DECISION_LABELS[decision!].toLowerCase()} saved`
      );
      setDecision(null);
      setReason('');
      onChanged();
    },
    // The reason stays in the box so nothing typed is lost.
    onError: (err) => setError(getErrorMessage(err)),
  });

  const open = (next: KycDocumentDecision) => {
    setDecision(next);
    setError(null);
  };

  return (
    <li className="py-3" data-testid={`kyc-document-${document.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="text-sm">
          <div className="font-medium text-gray-900 dark:text-white">
            {document.doc_type_display}
            {document.name ? ` · ${document.name}` : ''}
          </div>
          <div className="text-xs text-gray-500">
            {document.document_number_masked ? `No. ${document.document_number_masked} · ` : ''}
            {document.valid_until ? `Valid until ${document.valid_until}` : 'No expiry date'}
          </div>
          {document.remarks && (
            <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Reason: {document.remarks}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge size="sm" variant={documentStatusTone(document.status)}>
            {document.status_display}
          </Badge>
          {document.download_url && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                kycReviewService
                  .download(document)
                  .catch((err) => toast.error(getErrorMessage(err)))
              }
            >
              Download
            </Button>
          )}
          {(Object.keys(DECISION_LABELS) as KycDocumentDecision[]).map((d) => (
            <Button
              key={d}
              size="sm"
              variant={d === 'verify' ? 'primary' : d === 'reject' ? 'danger' : 'outline'}
              onClick={() => open(d)}
              disabled={review.isPending}
            >
              {DECISION_LABELS[d]}
            </Button>
          ))}
        </div>
      </div>
      {decision && (
        <form
          className="mt-3 space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (decisionNeedsReason(decision) && !reason.trim()) {
              setError('Give the broker a reason.');
              return;
            }
            setError(null);
            review.mutate();
          }}
        >
          <label className="block text-sm text-gray-700 dark:text-gray-300">
            {decisionNeedsReason(decision)
              ? 'Reason (sent to the broker)'
              : 'Note (optional, sent to the broker)'}
            <textarea
              className={inputClass}
              rows={2}
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" size="sm" isLoading={review.isPending}>
              {DECISION_LABELS[decision]}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setDecision(null)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </li>
  );
}

export function KycReviewDetailPage() {
  const { brokerId = '' } = useParams<{ brokerId: string }>();
  const queryClient = useQueryClient();
  const [changesReason, setChangesReason] = useState('');
  const [dossierError, setDossierError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['kyc-review', brokerId] });

  const summary = useQuery({
    queryKey: ['kyc-review', brokerId, 'summary'],
    queryFn: () => kycReviewService.summary(brokerId),
    enabled: !!brokerId,
  });
  const documents = useQuery({
    queryKey: ['kyc-review', brokerId, 'documents'],
    queryFn: () => kycReviewService.list<KycDocument>('documents', brokerId),
    enabled: !!brokerId,
  });
  const persons = useQuery({
    queryKey: ['kyc-review', brokerId, 'key-persons'],
    queryFn: () => kycReviewService.list<KycKeyPerson>('key-persons', brokerId),
    enabled: !!brokerId,
  });
  const indemnity = useQuery({
    queryKey: ['kyc-review', brokerId, 'indemnity'],
    queryFn: () => kycReviewService.list<KycIndemnity>('indemnity', brokerId),
    enabled: !!brokerId,
  });
  const banks = useQuery({
    queryKey: ['kyc-review', brokerId, 'bank-accounts'],
    queryFn: () => kycReviewService.list<KycBankAccount>('bank-accounts', brokerId),
    enabled: !!brokerId,
  });
  const financial = useQuery({
    queryKey: ['kyc-review', brokerId, 'financial'],
    queryFn: () => kycReviewService.list<KycFinancial>('financial', brokerId),
    enabled: !!brokerId,
  });

  const decide = useMutation({
    mutationFn: ({
      decision,
      remarks,
    }: {
      decision: 'verified' | 'changes_requested';
      remarks: string;
    }) => kycReviewService.decide(brokerId, decision, remarks),
    onSuccess: (_data, { decision }) => {
      toast.success(decision === 'verified' ? 'Dossier verified' : 'Changes requested');
      setChangesReason('');
      setDossierError(null);
      refresh();
    },
    onError: (err) => setDossierError(getErrorMessage(err)),
  });

  if (summary.isLoading) return <p className="text-sm text-gray-500">Loading dossier...</p>;
  if (summary.isError || !summary.data) {
    return (
      <div className="space-y-4">
        <BackButton to="/country/kyc-reviews" label="KYC reviews" />
        <p role="alert" className="text-sm text-red-600">
          Could not load this dossier{summary.error ? `: ${getErrorMessage(summary.error)}` : '.'}
        </p>
      </div>
    );
  }

  const data = summary.data;
  const blocked = verifyBlockedReason(data);

  return (
    <div className="space-y-6">
      <BackButton to="/country/kyc-reviews" label="KYC reviews" />

      <Card className="space-y-3 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{data.broker_name}</h1>
          <Badge variant={dossierStatusTone(data.kyc_status)}>
            {kycStatusLabel(data.kyc_status).label}
          </Badge>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {documentStateLabel(data.documents?.state)} · {data.completeness.done} of{' '}
          {data.completeness.total} requirements complete
          {data.kyc_submitted_at
            ? ` · submitted ${formatDate(data.kyc_submitted_at)}`
            : ''}
        </p>
        {data.missing.length > 0 && (
          <ul className="list-disc pl-5 text-sm text-amber-700">
            {data.missing.map((gap) => (
              <li key={gap.code}>{gap.message}</li>
            ))}
          </ul>
        )}
        {data.expiries.some((e) => e.state === 'expired' || e.state === 'expiring') && (
          <ul className="text-sm text-amber-700">
            {data.expiries
              .filter((e) => e.state === 'expired' || e.state === 'expiring')
              .map((e) => (
                <li key={`${e.kind}-${e.label}`}>
                  {e.label}: {e.state} on {e.valid_until}
                </li>
              ))}
          </ul>
        )}
        {data.licence && (
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Licence {data.licence.license_number || '-'} · {licenceRangeLabel(data.licence.license_valid_from, data.licence.license_valid_until)}
            {data.licence.licence_categories?.length
              ? ` · ${data.licence.licence_categories.join(', ')}`
              : ''}
          </p>
        )}
      </Card>

      <Card className="p-6">
        <CardHeader
          title="Documents"
          subtitle="Approve, reject or send back each document. The broker is notified by email."
        />
        {documents.isError ? (
          <p role="alert" className="text-sm text-red-600">
            Could not load the documents.
          </p>
        ) : !documents.data?.length ? (
          <p className="text-sm text-gray-500">No documents uploaded.</p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {documents.data.map((doc) => (
              <DocumentRow key={doc.id} document={doc} onChanged={refresh} />
            ))}
          </ul>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ReadOnlyList
          title="Key persons"
          rows={persons.data}
          isError={persons.isError}
          render={(p) =>
            `${p.role_display}: ${p.full_name}${p.pan_masked ? ` (PAN ${p.pan_masked})` : ''}${p.is_active ? '' : ' - inactive'}`
          }
        />
        <ReadOnlyList
          title="Professional indemnity"
          rows={indemnity.data}
          isError={indemnity.isError}
          render={(i) =>
            `${i.insurer_name} ${i.policy_number}: ${i.sum_insured}, ${i.valid_from} to ${i.valid_until}${i.is_current ? ' (current)' : ''}`
          }
        />
        <ReadOnlyList
          title="Bank accounts"
          rows={banks.data}
          isError={banks.isError}
          render={(b) =>
            `${b.bank_name} ${b.account_masked} (${b.ifsc})${b.is_primary ? ' - primary' : ''}`
          }
        />
        <ReadOnlyList
          title="Financial standing"
          rows={financial.data}
          isError={financial.isError}
          render={(f) =>
            `FY ${f.fy}: net worth ${f.net_worth ?? '-'}, deposit ${f.security_deposit_amount ?? '-'}`
          }
        />
      </div>

      <Card className="space-y-3 p-6">
        <CardHeader
          title="Dossier decision"
          subtitle="Verify once every document is approved, or send the whole dossier back."
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => decide.mutate({ decision: 'verified', remarks: '' })}
            disabled={!canVerifyDossier(data) || decide.isPending}
          >
            Verify dossier
          </Button>
          {blocked && <span className="text-sm text-gray-500">{blocked}</span>}
        </div>
        {data.kyc_status === 'submitted' && (
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (!changesReason.trim()) {
                setDossierError('Say what needs to change.');
                return;
              }
              decide.mutate({ decision: 'changes_requested', remarks: changesReason.trim() });
            }}
          >
            <label className="block text-sm text-gray-700 dark:text-gray-300">
              Request changes to the dossier (reason sent to the broker)
              <textarea
                className={inputClass}
                rows={2}
                maxLength={500}
                value={changesReason}
                onChange={(e) => setChangesReason(e.target.value)}
              />
            </label>
            <Button type="submit" variant="outline" disabled={decide.isPending}>
              Request changes
            </Button>
          </form>
        )}
        {dossierError && (
          <p role="alert" className="text-sm text-red-600">
            {dossierError}
          </p>
        )}
      </Card>
    </div>
  );
}
